import React, { useState, useEffect, useRef } from 'react';
import { Award, Film, Users, Heart, Star, Sparkles } from 'lucide-react';

const CareerJourney = ({ data }) => {
  const [isVisible, setIsVisible] = useState(true); // start visible to avoid blank-screen race on mobile direct-link landings
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Debug logging
  console.log('CareerJourney data:', data);

  if (!data) {
    return (
      <section id="journey" ref={sectionRef} className="py-24 bg-slate-900">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl text-white">No Journey Data Available</h2>
        </div>
      </section>
    );
  }

  return (
    <section 
      id="journey" 
      ref={sectionRef}
      className="relative py-24 bg-gradient-to-b from-teal-900/15 via-slate-900 to-blue-900/15 overflow-hidden"
    >
      <div className="relative z-10 container mx-auto px-6">
        <div className="max-w-7xl mx-auto">
          {/* Journey Header */}
          <div className={`text-center mb-20 transition-all duration-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <h2 className="text-5xl md:text-6xl font-extralight mb-6 tracking-wider text-teal-200">
              {data.title}
            </h2>
            <p className="text-2xl text-teal-300 font-light mb-4">{data.subtitle}</p>
            <p className="text-lg text-slate-400 max-w-4xl mx-auto leading-relaxed font-light">
              {data.description}
            </p>
          </div>

          {/* Simple Timeline - Reverse Chronological Order */}
          <div className="space-y-8">
            {data.timeline && data.timeline.slice().reverse().map((phase, index) => (
              <div key={index} className="bg-slate-800/50 rounded-2xl p-6">
                <h3 className="text-2xl text-blue-200 mb-2">{phase.phase}</h3>
                <div className="flex items-center space-x-4 mb-2">
                  <p className="text-slate-400">{phase.age}</p>
                  {phase.location && (
                    <>
                      <span className="text-slate-600">•</span>
                      <p className="text-slate-500 italic">{phase.location}</p>
                    </>
                  )}
                </div>
                <p className="text-slate-300">{phase.essence}</p>
                <p className="text-teal-300 italic mt-2">{phase.wisdom}</p>
                {phase.link && (
                  <button 
                    onClick={() => {
                      const element = document.getElementById(phase.link.replace('#', ''));
                      if (element) {
                        element.scrollIntoView({ behavior: 'instant', block: 'start' });
                      }
                    }}
                    className="inline-block mt-4 px-5 py-2.5 rounded-lg bg-teal-600/30 hover:bg-teal-500/40 text-teal-200 text-sm transition-all duration-300 hover:scale-105"
                  >
                    Learn More & Enroll →
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CareerJourney;